<template>
  <v2-row noSpace>
    <v2-col col12 noSpaceX noSpace>
      <v2-select
        :label="$t('quickLaunch.labels.textTemplate')"
        :placeholder="$t('quickLaunch.placeholders.selectTextTemplate')"
        v-model="textTemplateSnippet"
        name="textSnippets"
        optionLabel="name"
        :options="textTemplateSnippets"
        :selectLabel="$t('labels.select')"
        :selectedLabel="$t('labels.selected')"
        :deselectLabel="$t('labels.deselect')"
        @input="selectTextTemplate"
        @remove="textTemplateSnippet = ''"
      ></v2-select>
    </v2-col>
    <v2-col noSpaceX class="flex-row">
      <div>{{ $t("quickLaunch.labels.useOwn") }}</div>
      <v2-switch small :value="useOwn" @input="toggleUseOwn" :name="'addImage-' + component.uid"></v2-switch>
    </v2-col>
    <v2-col col12 noSpaceX noSpace class="relative">
      <div v-show="!useOwn" class="layover-redactor"></div>
      <v2-redactor
        :label="$t('quickLaunch.labels.content')"
        :placeholder="$t('forms.required')"
        minHeight="300px"
        v-model="textTemplateSnippetText"
        :config="redactorCustomConfig"
        v-validate="'required'"
        name="content"
        :error="errors.first('content')"
        @keyup.native="setChangedComp()"
        :data-vv-as="$t('quickLaunch.labels.content')"
      />
    </v2-col>
    <v2-col col12 class="items-end" noSpaceX noSpace>
      <v2-button outline primary @click.native="saveComponent(false)">{{ $t("quickLaunch.actions.save") }}</v2-button>
    </v2-col>
  </v2-row>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { tablePlugin, tableClass } from "@envivo/backoffice-components/_base_v2/inputs/redactor/redactorPlugins/table";

export default {
  name: "QuickLaunchTextTemplateForm",
  props: {
    textTemplateSnippets: {
      type: Array,
      default: function () {
        return [];
      },
    },
    defaultSnippet: {
      type: Object,
      default: function () {
        return {};
      },
    },
    component: Object,
  },
  data() {
    return {
      useOwn: false,
      hasChanged: false,
      textTemplateSnippet: null,
      textTemplateSnippetText: "",
      variables: [
        "${owner:first_name}",
        "${owner:last_name}",
        "${project:link}",
        "${contact:first_name}",
        "${contact:last_name}",
      ],
      redactorCustomConfig: {
        plugins: ["table"],
      },
    };
  },
  created() {
    this.preselectTextTemplate();
    if (this.editorTableIsEnabled) {
      window.Redactor.add("plugin", "table", tablePlugin);
      window.Redactor.add("class", "table.component", tableClass);
    }
    if (!this.textTemplateSnippet) {
      this.setDefaultTextTemplateSnippet();
    }
    if (this.component.value && this.component.value.useOwn && this.component.value.textContent) {
      this.useOwn = true;
      this.textTemplateSnippetText = this.component.value.textContent;
    } else {
      this.insertSnippet();
    }
    this.$eventbus.$on("saveComponents", () => {
      this.saveComponent(true);
    });
  },
  methods: {
    ...mapActions(["updateComponent", "createComponentRelation", "getProjectComponent"]),
    setChangedComp() {
      this.hasChanged = true;
      this.$eventbus.$emit("setUnsavedComponents", this.component.uid);
    },
    preselectTextTemplate() {
      if (this.component.relations[0]) {
        for (let i = 0; i < this.textTemplateSnippets.length; i++) {
          if (this.textTemplateSnippets[i].uid === this.component.relations[0].uid) {
            this.textTemplateSnippet = this.textTemplateSnippets[i];
          }
        }
      }
    },
    setDefaultTextTemplateSnippet() {
      if (this.defaultSnippet && this.defaultSnippet.uid) {
        this.textTemplateSnippet = this.defaultSnippet;
      }
      if (this.textTemplateSnippet) this.insertSnippet();
    },
    selectTextTemplate() {
      this.setChangedComp();
      this.useOwn = false;
      this.insertSnippet();
    },
    insertSnippet() {
      this.$nextTick(() => {
        if (
          Object.prototype.hasOwnProperty.call(this.textTemplateSnippet.content.body, this.currentProject.owner.locale)
        ) {
          if (
            Object.prototype.hasOwnProperty.call(
              this.textTemplateSnippet.content.body[this.currentProject.owner.locale],
              this.currentProject.owner.gender
            )
          ) {
            this.textTemplateSnippetText = this.replaceVars(
              this.textTemplateSnippet.content.body[this.currentProject.owner.locale][this.currentProject.owner.gender]
            );
          } else {
            this.textTemplateSnippetText = this.replaceVars(
              this.textTemplateSnippet.content.body[this.currentProject.owner.locale].default
            );
          }
        } else {
          if (
            Object.prototype.hasOwnProperty.call(
              this.textTemplateSnippet.content.body.default,
              this.currentProject.owner.gender
            )
          ) {
            this.textTemplateSnippetText = this.replaceVars(
              this.textTemplateSnippet.content.body.default[this.currentProject.owner.gender]
            );
          } else {
            this.textTemplateSnippetText = this.replaceVars(this.textTemplateSnippet.content.body.default.default);
          }
        }
      });
    },
    replaceVars(str) {
      this.variables.forEach((v) => {
        const replaceVal = this.getReplaceVal(v);
        const re = new RegExp(v.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"), "g");
        str = str.replace(re, replaceVal);
      });
      str = this.getReplacedMultiGenderVar(str);
      return str;
    },
    getReplacedMultiGenderVar(text) {
      var subgroup = "$3";
      switch (this.currentProject.owner.gender) {
        case "m": {
          subgroup = "$2";
          break;
        }
        case "f": {
          subgroup = "$1";
          break;
        }
        default: {
          break;
        }
      }
      return text.replace(/\${g:(.*?):(.*?):(.*?)}/g, subgroup);
    },
    getReplaceVal(variable) {
      switch (variable) {
        case "${owner:first_name}":
          return this.currentProject.owner.first_name;
        case "${owner:last_name}":
          return this.currentProject.owner.last_name;
        case "${currentProject:link}":
          return this.currentProject.links.frontend;
        case "${contact:first_name}":
          return this.currentProject.contact ? this.currentProject.contact.first_name : "";
        case "${contact:last_name}":
          return this.currentProject.contact ? this.currentProject.contact.last_name : "";
      }
    },
    toggleUseOwn() {
      this.setChangedComp();
      this.useOwn = !this.useOwn;
      if (!this.useOwn) {
        this.insertSnippet();
      }
    },
    saveComponent(isEvent = false) {
      if (this.hasChanged) {
        const vm = this;
        const data = {
          customLoading: true,
          params: {
            project: vm.currentProject.uid,
            component: vm.component.uid,
          },
          data: {
            value: {
              useOwn: vm.useOwn,
              textContent: vm.textTemplateSnippetText
                ? vm.$base64.encode(vm.textTemplateSnippetText)
                : vm.textTemplateSnippetText,
            },
            encoded: ["textContent"],
          },
        };
        vm.$validator.validateAll().then((result) => {
          if (result) {
            const d = {
              customLoading: true,
              params: {
                project: vm.currentProject.uid,
                component: vm.component.uid,
              },
              data: {
                uid: vm.textTemplateSnippet.uid,
                type: "text_template",
              },
            };
            vm.createComponentRelation(d)
              .then(() => {
                vm.updateComponent(data)
                  .then(() => {
                    vm.$eventbus.$emit("componentSavedSuccess", this.component.uid, isEvent);
                    vm.hasChanged = false;
                    if (!isEvent) {
                      vm.$snotify.success(vm.$t("quickLaunch.toasts.saveComponent.success"), vm.$t("toasts.success"), {
                        icon: vm.$successIcon,
                      });
                      vm.reloadComponent();
                    }
                  })
                  .catch(() => {
                    vm.$eventbus.$emit("componentSavedError", this.component.uid);
                    if (!isEvent) {
                      vm.$snotify.error(vm.$t("quickLaunch.toasts.saveComponent.error"), vm.$t("toasts.error"), {
                        timeout: 0,
                        icon: vm.$errorIcon,
                      });
                    }
                  });
              })
              .catch(() => {
                vm.$eventbus.$emit("componentSavedError", this.component.uid);
                if (!isEvent) {
                  vm.$snotify.error(vm.$t("quickLaunch.toasts.saveComponent.error"), vm.$t("toasts.error"), {
                    timeout: 0,
                    icon: vm.$errorIcon,
                  });
                }
              });
          } else {
            vm.$eventbus.$emit("componentSavedError", this.component.uid);
            if (!isEvent) {
              vm.$snotify.error(vm.$t("quickLaunch.toasts.saveComponent.validation"), vm.$t("toasts.error"), {
                timeout: 0,
                icon: vm.$errorIcon,
              });
            }
          }
        });
      }
    },
    reloadComponent() {
      this.getProjectComponent({
        customLoading: true,
        params: {
          project: this.currentProject.uid,
          component: this.component.uid,
        },
      }).then(() => {});
    },
  },
  computed: {
    ...mapGetters({
      teams: "auth_get_teams",
      currentProject: "projects_get_current_project",
    }),
    editorTableIsEnabled() {
      return this.teams[this.$route.params.team].account.team.attributes.editor_table_enabled;
    },
  },
};
</script>
